import React from "react";

/**
 * PetDetailsEditor (Updated)
 * -------------------------
 * ✅ Works with multi‑pet state
 * ✅ Shows existing data in edit mode
 * ✅ Dropdown binds for Type → Breed
 * ✅ No image upload section
 *
 * REQUIRED PROPS:
 *  pets
 *  setPets
 *  petTypeOptions
 *  petBreedOptionsByType (fn)
 *  onPickBreed (optional)
 *  crateSizeOptions
 */

export const petTemplate = {
  petdetail_id: "",
  pet_name: "",
  pet_type: "",
  pet_breed: "",
  pet_gender: "",
  pet_behaviour: "",
  pet_dob: "",
  pet_age: "",
  pet_weight: "",
  chargeable_wt: "",
  crate_size: "",
  pet_length: "",
  pet_width: "",
  pet_height: "",
  container_length: "",
};

export default function PetDetailsEditor({
  pets,
  setPets,
  petTypeOptions = [],
  petBreedOptionsByType = () => [],
  onPickBreed,
  crateSizeOptions = {},
}) {
  const updatePet = (idx, field, value) => {
    setPets((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  };

  const addPet = () => setPets((prev) => [...prev, { ...petTemplate }]);

  const removePet = (idx) => setPets((prev) => prev.filter((_, i) => i !== idx));

  const handleDOB = (idx, value) => {
    const age = calcAge(value);
    setPets((prev) => prev.map((p, i) => (i === idx ? { ...p, pet_dob: value, pet_age: age } : p)));
  };

  const handleCrateChange = (idx, value) => {
    const weightMap = { "17": 17, "35": 35, "48": 48, "66": 66, "89": 89, "147": 147 };
    const cw =
      value === "CR82"
        ? ""
        : value === "Soft Bag"
        ? 0
        : weightMap[value] ?? "";

    setPets((prev) => prev.map((p, i) => (i === idx ? { ...p, crate_size: value, chargeable_wt: cw } : p)));
  };

  return (
    <div className="space-y-6">
      {pets?.map((pet, idx) => (
        <div key={idx} className="rounded-2xl shadow p-3 border">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold">Pet #{idx + 1}</h2>
            {pets.length > 1 && (
              <button type="button" className="btn btn-danger" onClick={() => removePet(idx)}>
                Remove
              </button>
            )}
          </div>

          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Name</label>
              <input
                className="form-control"
                value={pet.pet_name || ""}
                onChange={(e) => updatePet(idx, "pet_name", e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Type</label>
              <select
                className="form-control"
                value={pet.pet_type || ""}
                onChange={(e) => {
                  updatePet(idx, "pet_type", e.target.value);
                  updatePet(idx, "pet_breed", "");
                  onPickBreed?.(e.target.value);
                }}
              >
                <option value="">Select</option>
                {petTypeOptions.map((pt) => (
                  <option key={pt.pettype} value={pt.pettype}>
                    {pt.pettype_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Breed</label>
              <select
                className="form-control"
                value={pet.pet_breed || ""}
                onChange={(e) => updatePet(idx, "pet_breed", e.target.value)}
              >
                <option value="">Select</option>
                {petBreedOptionsByType?.(pet.pet_type)?.map((b) => (
                  <option key={b.PetBreedID} value={b.PetBreedID}>
                    {b.BreedName}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Gender</label>
              <select
                className="form-control"
                value={pet.pet_gender || ""}
                onChange={(e) => updatePet(idx, "pet_gender", e.target.value)}
              >
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">DOB</label>
              <input
                type="date"
                className="form-control"
                value={pet.pet_dob || ""}
                onChange={(e) => handleDOB(idx, e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Age</label>
              <input className="form-control" value={pet.pet_age || ""} readOnly />
            </div>

            <div className="col-md-3">
              <label className="form-label">Actual Weight (kg)</label>
              <input
                className="form-control"
                value={pet.pet_weight || ""}
                onChange={(e) => updatePet(idx, "pet_weight", e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Crate Size</label>
              <select
                className="form-control"
                value={pet.crate_size || ""}
                onChange={(e) => handleCrateChange(idx, e.target.value)}
              >
                {Object.entries(crateSizeOptions).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Chargeable Wt (kg)</label>
              <input
                className="form-control"
                value={pet.chargeable_wt || ""}
                onChange={(e) => updatePet(idx, "chargeable_wt", e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Length (in)</label>
              <input
                className="form-control"
                value={pet.pet_length || ""}
                onChange={(e) => updatePet(idx, "pet_length", e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Width (in)</label>
              <input
                className="form-control"
                value={pet.pet_width || ""}
                onChange={(e) => updatePet(idx, "pet_width", e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Height (in)</label>
              <input
                className="form-control"
                value={pet.pet_height || ""}
                onChange={(e) => updatePet(idx, "pet_height", e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Container Length (in)</label>
              <input
                className="form-control"
                value={pet.container_length || ""}
                onChange={(e) => updatePet(idx, "container_length", e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Behaviour</label>
              <input
                className="form-control"
                value={pet.pet_behaviour || ""}
                onChange={(e) => updatePet(idx, "pet_behaviour", e.target.value)}
              />
            </div>
          </div>
        </div>
      ))}

      <div className="d-flex justify-content-between mt-3">
        <button type="button" className="btn btn-secondary" onClick={addPet}>
          + Add Pet
        </button>
        <span>Total Pets: {pets?.length || 0}</span>
      </div>
    </div>
  );
}

function calcAge(dob) {
  if (!dob) return "";
  const d = new Date(dob);
  const t = new Date();
  let years = t.getFullYear() - d.getFullYear();
  let months = t.getMonth() - d.getMonth();
  let days = t.getDate() - d.getDate();
  if (days < 0) {
    months--;
    days += new Date(t.getFullYear(), t.getMonth(), 0).getDate();
  }
  if (months < 0) {
    years--;
    months += 12;
  }
  return `${years}y ${months}m ${days}d`;
}
